import { h } from "snabbdom";
import type { VNode } from "snabbdom";
import type { Model, Msg, TokenScope } from "../types";

export function viewSettingsPage(model: Model, dispatch: (msg: Msg) => void): VNode {
  return h("section.settings-page", [
    h("div.page__head", [
      h("h1.page__title", "Settings"),
      h(
        "button.page__back",
        { on: { click: () => dispatch({ type: "GoHome" }) } },
        "← Back"
      ),
    ]),
    viewTokens(model, dispatch),
  ]);
}

// API tokens: the durable credential an agent uses, because a session ends at
// every server restart.
function viewTokens(model: Model, dispatch: (msg: Msg) => void): VNode {
  return h("section.tokens", [
    h("h2.page__subhead", "API tokens"),
    ...(model.newTokenSecret ? [viewNewToken(model.newTokenSecret, dispatch)] : []),
    h(
      "div.tokens__list",
      model.tokens.length === 0
        ? [h("p.page__empty", "No tokens yet.")]
        : model.tokens.map((token) =>
            h("div.token-row", [
              h("span.token-row__name", token.name),
              h(
                "span.token-row__scope",
                token.scope === "read" ? "read only" : "read and write"
              ),
              h("span.token-row__date", token.createdAt.slice(0, 10)),
              h(
                "button.token-row__delete",
                {
                  attrs: { title: "Revoke token" },
                  on: { click: () => dispatch({ type: "DeleteToken", id: token.id }) },
                },
                "🗑"
              ),
            ])
          )
    ),
    h("div.tokens__new", [
      h("input.tokens__name", {
        attrs: { placeholder: "token name…" },
        props: { value: model.tokenDraftName },
        on: {
          input: (e: Event) =>
            dispatch({ type: "SetTokenDraftName", name: (e.target as HTMLInputElement).value }),
        },
      }),
      h(
        "select.tokens__scope",
        {
          on: {
            change: (e: Event) =>
              dispatch({
                type: "SetTokenDraftScope",
                scope: (e.target as HTMLSelectElement).value as TokenScope,
              }),
          },
        },
        [
          h(
            "option",
            { attrs: { value: "read_write", selected: model.tokenDraftScope === "read_write" } },
            "read and write"
          ),
          h(
            "option",
            { attrs: { value: "read", selected: model.tokenDraftScope === "read" } },
            "read only"
          ),
        ]
      ),
      h(
        "button.tokens__create",
        {
          attrs: { disabled: model.tokenDraftName.trim() === "" },
          on: { click: () => dispatch({ type: "CreateToken" }) },
        },
        "Create"
      ),
    ]),
  ]);
}

// The page shows the secret one time only. It leaves the model as soon as the
// user hides it, so every integration guide belongs in this one panel.
function viewNewToken(secret: string, dispatch: (msg: Msg) => void): VNode {
  const base = window.location.origin;
  return h("div.tokens__secret", [
    h("p.tokens__secret-label", "Copy this token now. It cannot be shown again."),
    viewSnippet(secret),
    h("div.tokens__secret-actions", [
      h(
        "button.tokens__guide-copy",
        { on: { click: () => dispatch({ type: "DismissNewToken" }) } },
        "Hide"
      ),
    ]),
    h("div.tokens__guides", [
      viewGuide("Claude.ai", [
        { text: "Open the settings of Claude.ai, then choose Connectors." },
        { text: "Choose “Add custom connector”." },
        { text: "Give the connector a name, then paste this URL.", snippet: `${base}/api/mcp` },
        { text: "For Authentication, choose “None”. Cardlake uses a token, not OAuth." },
        { text: "Under “Request headers”, choose this header name.", snippet: "authorization" },
        {
          text: "Paste this header value. Keep the “Required” box ticked.",
          snippet: `Bearer ${secret}`,
        },
        { text: "Choose Add." },
      ]),
      viewGuide("Claude Code", [
        {
          text: "Run this command in a terminal, in any directory. “--scope user” adds the server to every project; without it, it belongs to one project only.",
          snippet: `claude mcp add --scope user --transport http cardlake ${base}/api/mcp \\\n  --header "Authorization: Bearer ${secret}"`,
        },
      ]),
    ]),
  ]);
}

// One step of a guide. A step that needs a value carries it, so the value
// stands where the user needs it.
type GuideStep = { text: string; snippet?: string };

// One integration guide, folded until the user opens it.
function viewGuide(name: string, steps: GuideStep[]): VNode {
  return h("details.tokens__guide", [
    h("summary.tokens__guide-name", name),
    h(
      "ol.tokens__guide-steps",
      steps.map((step) =>
        h("li", [
          step.text,
          ...(step.snippet === undefined ? [] : [viewSnippet(step.snippet)]),
        ])
      )
    ),
  ]);
}

// A value with its copy button at the right of it.
function viewSnippet(value: string): VNode {
  return h("div.tokens__guide-field", [
    h("pre.tokens__guide-snippet", value),
    h(
      "button.tokens__guide-copy",
      { on: { click: (e: Event) => copyValue(value, e.currentTarget as HTMLElement) } },
      "Copy"
    ),
  ]);
}

// How long the button says "Copied", in milliseconds.
const COPIED_MS = 1500;

// Copy the value, then say so on the button. The label is local to one button
// and it disappears again, so it stays out of the model.
function copyValue(value: string, button: HTMLElement): void {
  void navigator.clipboard.writeText(value).then(
    () => showCopied(button, "Copied"),
    () => showCopied(button, "Failed")
  );
}

function showCopied(button: HTMLElement, label: string): void {
  button.textContent = label;
  button.classList.add("tokens__guide-copy--done");
  window.setTimeout(() => {
    button.textContent = "Copy";
    button.classList.remove("tokens__guide-copy--done");
  }, COPIED_MS);
}
